import logger from "../utils/logger";
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Users, Plus, Search, Edit, Trash2, Mail, Phone, Building2, MapPin, FileText, Link2, Download } from 'lucide-react';
import EmployeeDocuments from '../components/EmployeeDocuments';
import ChangeHistory from '../components/ChangeHistory';
import { Link } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

import API_URL from '../utils/api';

const Employees = () => {
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [officeLocations, setOfficeLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [docsEmployee, setDocsEmployee] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        password: '',
        role: 'employee',
        employee_type: 'office',
        department: '',
        designation: '',
        phone: '',
        address: '',
        date_of_joining: '',
        basic_salary: '',
        bank_account: '',
        bank_name: '',
        assigned_office_id: ''
    });

    const fetchData = useCallback(async () => {
        try {
            const [empRes, deptRes, officeRes] = await Promise.all([
                axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
                axios.get(`${API_URL}/api/departments`, { withCredentials: true }),
                axios.get(`${API_URL}/api/office-locations`, { withCredentials: true })
            ]);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
            setDepartments(deptRes.data);
            setOfficeLocations(officeRes.data);
        } catch (error) {
            logger.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                basic_salary: parseFloat(formData.basic_salary) || 0,
                date_of_joining: new Date(formData.date_of_joining).toISOString(),
                assigned_office_id: formData.assigned_office_id || null
            };

            if (editingEmployee) {
                // Update employee
                const { email, password, date_of_joining, ...updateData } = payload;
                await axios.put(`${API_URL}/api/employees/${editingEmployee._id}`, updateData, { withCredentials: true });
            } else {
                // Create employee
                await axios.post(`${API_URL}/api/employees`, payload, { withCredentials: true });
            }
            
            setIsDialogOpen(false);
            setEditingEmployee(null);
            resetForm();
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error saving employee');
        }
    };

    const handleEdit = (employee) => {
        setEditingEmployee(employee);
        setFormData({
            email: employee.email || '',
            name: employee.name || '',
            password: '',
            role: employee.role || 'employee',
            employee_type: employee.employee_type || 'office',
            department: employee.department || '',
            designation: employee.designation || '',
            phone: employee.phone || '',
            address: employee.address || '',
            date_of_joining: employee.date_of_joining ? employee.date_of_joining.split('T')[0] : '',
            basic_salary: employee.basic_salary?.toString() || '',
            bank_account: employee.bank_account || '',
            bank_name: employee.bank_name || '',
            assigned_office_id: employee.assigned_office_id || ''
        });
        setIsDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this employee?')) return;
        try {
            await axios.delete(`${API_URL}/api/employees/${id}`, { withCredentials: true });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error deleting employee');
        }
    };

    const resetForm = () => {
        setFormData({
            email: '',
            name: '',
            password: '',
            role: 'employee',
            employee_type: 'office',
            department: '',
            designation: '',
            phone: '',
            address: '',
            date_of_joining: '',
            basic_salary: '',
            bank_account: '',
            bank_name: '',
            assigned_office_id: ''
        });
    };

    const filteredEmployees = employees.filter(emp => 
        emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.department?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading employees...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="employees-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Employees
                    </h1>
                    <p className="text-slate-500 mt-1">Manage your workforce</p>
                </div>
                
                <Dialog open={isDialogOpen} onOpenChange={(open) => {
                    setIsDialogOpen(open);
                    if (!open) {
                        setEditingEmployee(null);
                        resetForm();
                    }
                }}>
                    <div className="flex flex-wrap gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="rounded-lg border-blue-200 text-brand hover:bg-blue-50" data-testid="download-employees-btn">
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-xl">
                                <DropdownMenuItem className="cursor-pointer rounded-md mx-1" onClick={async () => {
                                    try {
                                        const response = await axios.get(`${API_URL}/api/reports/employees?format=excel`, { withCredentials: true, responseType: 'blob' });
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const a = document.createElement('a');
                                        a.href = url; a.download = `employees_${new Date().toISOString().slice(0,10)}.xlsx`; a.click();
                                        window.URL.revokeObjectURL(url);
                                        toast.success('Employee list downloaded');
                                    } catch (e) { toast.error('Download failed'); }
                                }} data-testid="download-employees-excel">
                                    <FileText className="h-4 w-4 mr-2" />Excel (.xlsx)
                                </DropdownMenuItem>
                                <DropdownMenuItem className="cursor-pointer rounded-md mx-1" onClick={async () => {
                                    try {
                                        const response = await axios.get(`${API_URL}/api/reports/employees?format=csv`, { withCredentials: true, responseType: 'blob' });
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const a = document.createElement('a');
                                        a.href = url; a.download = `employees_${new Date().toISOString().slice(0,10)}.csv`; a.click();
                                        window.URL.revokeObjectURL(url);
                                        toast.success('Employee list downloaded');
                                    } catch (e) { toast.error('Download failed'); }
                                }} data-testid="download-employees-csv">
                                    <FileText className="h-4 w-4 mr-2" />CSV (.csv)
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <Link to="/onboarding?tab=invites&action=generate">
                            <Button variant="outline" className="rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="share-onboarding-link-employees-btn">
                                <Link2 className="h-4 w-4 mr-2" />
                                Share Onboarding Link
                            </Button>
                        </Link>
                        <DialogTrigger asChild>
                            <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="add-employee-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Employee
                            </Button>
                        </DialogTrigger>
                    </div>
                    <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                            </DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Provide the details below to continue.</DialogDescription>
                        </DialogHeader>
                        {!editingEmployee && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 text-xs text-emerald-800 flex items-center gap-2">
                                <Link2 className="h-3.5 w-3.5 flex-shrink-0" />
                                <span>Tip: Skip filling all this — <Link to="/onboarding?tab=invites&action=generate" className="font-bold underline hover:no-underline">share an onboarding link</Link> and let the new hire register themselves.</span>
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Full Name</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        required
                                        data-testid="employee-name-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        required
                                        disabled={!!editingEmployee}
                                        data-testid="employee-email-input"
                                    />
                                </div>
                                {!editingEmployee && (
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Password</Label>
                                        <Input
                                            type="password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                                            className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                            required={!editingEmployee}
                                            data-testid="employee-password-input"
                                        />
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Role</Label>
                                    <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                                        <SelectTrigger className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="employee-role-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-lg">
                                            <SelectItem value="employee">RM (Relationship Manager)</SelectItem>
                                            <SelectItem value="hr_manager">HR Manager</SelectItem>
                                            <SelectItem value="operations_manager">Ops Manager</SelectItem>
                                            <SelectItem value="operations_executive">Ops Executive</SelectItem>
                                            <SelectItem value="manager">Team Manager</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Employee Type</Label>
                                    <Select value={formData.employee_type} onValueChange={(value) => setFormData({...formData, employee_type: value})}>
                                        <SelectTrigger className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="employee-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-lg">
                                            <SelectItem value="office">Head Office</SelectItem>
                                            <SelectItem value="field">Field Work</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {formData.employee_type === 'office' && officeLocations.length > 0 && (
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Assigned Office</Label>
                                        <Select value={formData.assigned_office_id || "any"} onValueChange={(value) => setFormData({...formData, assigned_office_id: value === "any" ? "" : value})}>
                                            <SelectTrigger className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="assigned-office-select">
                                                <SelectValue placeholder="Any office (optional)" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-lg">
                                                <SelectItem value="any">Any Office</SelectItem>
                                                {officeLocations.map(office => (
                                                    <SelectItem key={office._id} value={office._id}>{office.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Department</Label>
                                    <Select value={formData.department} onValueChange={(value) => setFormData({...formData, department: value})}>
                                        <SelectTrigger className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="employee-department-select">
                                            <SelectValue placeholder="Select department" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-lg">
                                            {/* Default trio (HR, Operations, Sales) — merged with admin-added customs, deduped */}
                                            {Array.from(new Set(['HR', 'Operations', 'Sales', ...departments.map(d => d.name)])).map(name => (
                                                <SelectItem key={name} value={name}>{name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Designation</Label>
                                    <Input
                                        value={formData.designation}
                                        onChange={(e) => setFormData({...formData, designation: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        required
                                        data-testid="employee-designation-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
                                    <Input
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="employee-phone-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Date of Joining</Label>
                                    <Input
                                        type="date"
                                        value={formData.date_of_joining}
                                        onChange={(e) => setFormData({...formData, date_of_joining: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        required
                                        data-testid="employee-doj-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Basic Salary</Label>
                                    <Input
                                        type="number"
                                        value={formData.basic_salary}
                                        onChange={(e) => setFormData({...formData, basic_salary: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        required
                                        data-testid="employee-salary-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Bank Account</Label>
                                    <Input
                                        value={formData.bank_account}
                                        onChange={(e) => setFormData({...formData, bank_account: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="employee-bank-account-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Bank Name</Label>
                                    <Input
                                        value={formData.bank_name}
                                        onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                                        className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                        data-testid="employee-bank-name-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
                                <Input
                                    value={formData.address}
                                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                                    className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                    data-testid="employee-address-input"
                                />
                            </div>

                            {editingEmployee && (
                                <div className="border-t border-slate-200 pt-4">
                                    <ChangeHistory resourceType="employee" resourceId={editingEmployee._id} limit={20} compact />
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-lg" onClick={() => setIsDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="employee-save-btn">
                                    {editingEmployee ? 'Update' : 'Create'} Employee
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Search */}
            <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                            placeholder="Search employees by name, email, or department..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 rounded-lg bg-slate-50 border-slate-200"
                            data-testid="employee-search-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Employee List */}
            <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                <CardHeader className="border-b border-slate-200 bg-slate-50/50">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Users className="h-5 w-5 text-[#2563eb]" />
                        All Employees ({filteredEmployees.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="employees-table">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Employee</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Department</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Designation</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Contact</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmployees.map((emp) => (
                                    <tr key={emp._id} className="border-b border-slate-200 hover:bg-slate-50">
                                        <td className="p-4">
                                            <div>
                                                <p className="font-medium text-slate-900">{emp.name}</p>
                                                <p className="text-sm text-slate-500">{emp.employee_id}</p>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <Building2 className="h-4 w-4 text-slate-400" />
                                                <span className="text-slate-700">{emp.department || 'N/A'}</span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-slate-700">{emp.designation || 'N/A'}</td>
                                        <td className="p-4">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                                    <Mail className="h-3 w-3" />
                                                    {emp.email}
                                                </div>
                                                {emp.phone && (
                                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                                        <Phone className="h-3 w-3" />
                                                        {emp.phone}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <Badge className={`rounded-full px-3 py-0.5 ${emp.is_active !== false ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                                                {emp.is_active !== false ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="rounded-lg"
                                                    onClick={() => handleEdit(emp)}
                                                    data-testid={`edit-employee-${emp._id}`}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="rounded-lg"
                                                    onClick={() => setDocsEmployee(emp)}
                                                    data-testid={`docs-employee-${emp._id}`}
                                                >
                                                    <FileText className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    onClick={() => handleDelete(emp._id)}
                                                    data-testid={`delete-employee-${emp._id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredEmployees.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-slate-500">
                                            No employees found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Employee Documents Panel */}
            {docsEmployee && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Documents: {docsEmployee.name}
                        </h2>
                        <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => setDocsEmployee(null)}>Close</Button>
                    </div>
                    <EmployeeDocuments employeeId={docsEmployee._id} employeeName={docsEmployee.name} canManage={true} />
                </div>
            )}
        </div>
    );
};

export default Employees;
